import { createSlice } from "@reduxjs/toolkit";
import axios from "../../utils/axios";

const backendUrl = process.env.REACT_APP_BACKEND_URL;

const initialState = {
  isLoading: false,
  error: null,
  stockTransfers: [],
  stockTransfer: null,
};

const slice = createSlice({
  name: "stockTransfer",
  initialState,
  reducers: {
    startLoading(state) {
      state.isLoading = true;
      state.error = null;
    },
    hasError(state, action) {
      state.isLoading = false;
      state.error = action.payload;
    },
    getStockTransfersSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.stockTransfers = action.payload;
    },
    getStockTransferSuccess(state, action) {
      state.isLoading = false;
      state.error = null;
      state.stockTransfer = action.payload;
    },
    resetStockTransferStore() {
      return initialState;
    },
  },
});

export default slice.reducer;

export function getStockTransfers(warehouseId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`${backendUrl}/stockTransfer`, {
        params: {
          warehouseId,
          isActiveStockTransfer: false,
        },
      });

      const transfers = response.data?.results ?? response.data;

      if (!Array.isArray(transfers)) {
        throw new Error("Invalid Stock Transfer list response.");
      }

      dispatch(slice.actions.getStockTransfersSuccess(transfers));

      return {
        data: transfers,
        status: response.status,
      };
    } catch (error) {
      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function getStockTransfer(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(`${backendUrl}/stockTransfer/${id}`);

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function openStockTransferToEdit(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.patch(
        `${backendUrl}/stockTransfer/openStockTransferToEdit/${id}`,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function getActiveStockTransfer(warehouseId, warehouseUserId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.get(
        `${backendUrl}/stockTransfer/activeStockTransfer`,
        {
          params: {
            warehouseId,
            warehouseUserId,
          },
        },
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function addWarehouseItemInStockTransfer(data, warehouseItemId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.patch(
        `${backendUrl}/stockTransfer/addWarehouseItemInStockTransfer/${warehouseItemId}`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function updateWarehouseItemInStockTransfer(data, itemId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.patch(
        `${backendUrl}/stockTransfer/updateWarehouseItemInStockTransfer/${itemId}`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function deleteWarehouseItemFromStockTransfer(data, warehouseItemId) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.patch(
        `${backendUrl}/stockTransfer/deleteWarehouseItemFromStockTransfer/${warehouseItemId}`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function createStockTransfer(data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(`${backendUrl}/stockTransfer`, data);

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function updateStockTransfer(id, data) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.patch(
        `${backendUrl}/stockTransfer/${id}`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function dispatchStockTransfer(id, data = {}) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.post(
        `${backendUrl}/stockTransfer/${id}/dispatch`,
        data,
      );

      dispatch(slice.actions.getStockTransferSuccess(response.data));

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function deleteStockTransfer(id) {
  return async (dispatch) => {
    dispatch(slice.actions.startLoading());

    try {
      const response = await axios.delete(`${backendUrl}/stockTransfer/${id}`);

      dispatch(slice.actions.resetStockTransferStore());

      return {
        data: response?.data,
        status: response.status,
      };
    } catch (error) {
      console.error(error);

      dispatch(slice.actions.hasError(error));

      throw error;
    }
  };
}

export function clearStockTransferAfterUpdating() {
  return async (dispatch) => {
    dispatch(slice.actions.resetStockTransferStore());
  };
}
